import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiUrl } from "../../config/api";
import ConfirmDialog from "../globalcontrols/confirmdialog/ConfirmDialog";
import "./tableeCarousel.css";

const emptyItem = {
  linkedProductId: "",
  sortOrder: "",
  isActive: true,
};

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function TableeCarousel() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyItem);
  const [showModal, setShowModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [message, setMessage] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [productDropdownRect, setProductDropdownRect] = useState(null);
  const productSelectTriggerRef = useRef(null);

  async function loadData() {
    const headers = getSessionHeaders();
    const [carouselResponse, productsResponse] = await Promise.all([
      fetch(apiUrl("/tablee/carousel"), { headers }),
      fetch(apiUrl("/tablee/products"), { headers }),
    ]);
    const carouselData = await carouselResponse.json();
    const productsData = await productsResponse.json();

    if (carouselData.success) setItems(carouselData.items || []);
    else setMessage(carouselData.message || "No se pudo cargar el carrusel.");

    if (productsData.success) setProducts(productsData.products || []);
  }

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!showModal) return undefined;
    function handleEscape(event) {
      if (event.key === "Escape" && isProductDropdownOpen) {
        setIsProductDropdownOpen(false);
        return;
      }
      if (event.key === "Escape") closeModal();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showModal, isProductDropdownOpen]);

  useEffect(() => {
    if (!isProductDropdownOpen) return undefined;
    function handleOutsideClick(event) {
      if (
        !event.target.closest(".tablee-carousel__product-select") &&
        !event.target.closest(".tablee-carousel__product-select-menu")
      ) {
        setIsProductDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isProductDropdownOpen]);

  useEffect(() => {
    if (!isProductDropdownOpen || !productSelectTriggerRef.current) return undefined;

    function updateDropdownRect() {
      const rect = productSelectTriggerRef.current.getBoundingClientRect();
      setProductDropdownRect({
        left: rect.left,
        top: rect.bottom + 6,
        width: rect.width,
      });
    }

    updateDropdownRect();
    window.addEventListener("resize", updateDropdownRect);
    window.addEventListener("scroll", updateDropdownRect, true);
    return () => {
      window.removeEventListener("resize", updateDropdownRect);
      window.removeEventListener("scroll", updateDropdownRect, true);
    };
  }, [isProductDropdownOpen]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function openModal() {
    setForm({
      ...emptyItem,
      sortOrder: String(items.length + 1),
    });
    setMessage("");
    setProductSearch("");
    setIsProductDropdownOpen(false);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setForm(emptyItem);
    setProductSearch("");
    setIsProductDropdownOpen(false);
  }

  async function saveItem(event) {
    event.preventDefault();
    setMessage("");

    if (!form.linkedProductId) {
      setMessage("Selecciona un producto del catalogo.");
      return;
    }

    const response = await fetch(apiUrl("/tablee/carousel"), {
      method: "POST",
      headers: getSessionHeaders(),
      body: JSON.stringify({
        linkedProductId: Number(form.linkedProductId),
        sortOrder: Number(form.sortOrder || 1),
        isActive: form.isActive,
      }),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo agregar el elemento.");
      return;
    }

    closeModal();
    await loadData();
  }

  function deleteItem(item) {
    setConfirmDialog({
      title: "Eliminar elemento",
      message: `Eliminar "${item.title}" del carrusel Tablee?`,
      confirmLabel: "Eliminar",
      onConfirm: () => confirmDeleteItem(item),
    });
  }

  async function confirmDeleteItem(item) {
    setConfirmDialog(null);
    setMessage("");
    const response = await fetch(apiUrl(`/tablee/carousel/${item.id}`), {
      method: "DELETE",
      headers: getSessionHeaders(),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo eliminar el elemento.");
      return;
    }

    setMessage("Elemento eliminado.");
    await loadData();
  }

  async function toggleItemActive(item) {
    setItems((current) =>
      current.map((entry) => entry.id === item.id ? { ...entry, isActive: !item.isActive } : entry)
    );

    const response = await fetch(apiUrl(`/tablee/carousel/${item.id}/active`), {
      method: "PATCH",
      headers: getSessionHeaders(),
      body: JSON.stringify({ isActive: !item.isActive }),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo actualizar el estado.");
      await loadData();
    }
  }

  const selectedProduct = products.find((product) => String(product.id) === String(form.linkedProductId));
  const selectedProductImage = selectedProduct?.imageUrls?.[0] || selectedProduct?.imageUrl || "/images/tablee_logo.png";
  const normalizedProductSearch = productSearch.trim().toLowerCase();
  const filteredProducts = normalizedProductSearch
    ? products.filter((product) => (
      [
        product.name,
        product.sku,
        product.category,
        product.subcategory,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedProductSearch)
    ))
    : products;
  const productDropdownMenu = isProductDropdownOpen && productDropdownRect ? createPortal(
    <div
      className="tablee-carousel__product-select-menu"
      role="listbox"
      style={{ left: productDropdownRect.left, top: productDropdownRect.top, width: productDropdownRect.width }}
    >
      <label className="tablee-carousel__product-select-search">
        <span>Buscar producto</span>
        <input
          value={productSearch}
          placeholder="Nombre, SKU o categoria"
          autoFocus
          onChange={(event) => setProductSearch(event.target.value)}
        />
      </label>
      <div className="tablee-carousel__product-select-options">
        {filteredProducts.length ? filteredProducts.map((product) => (
          <button
            key={product.id}
            type="button"
            role="option"
            aria-selected={String(product.id) === String(form.linkedProductId)}
            className={String(product.id) === String(form.linkedProductId) ? "is-selected" : ""}
            onClick={() => {
              updateField("linkedProductId", String(product.id));
              setProductSearch("");
              setIsProductDropdownOpen(false);
            }}
          >
            <strong>{product.name}</strong>
            <small>{[product.sku, product.category, product.subcategory].filter(Boolean).join(" · ")}</small>
          </button>
        )) : (
          <span className="tablee-carousel__product-select-empty">Sin productos encontrados</span>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="tablee-carousel">
      <header className="tablee-carousel__header">
        <img src="/images/tablee_logo.png" alt="Tablee" />
        <div>
          <span>Tablee</span>
          <h1>Carrusel principal</h1>
          <p>Administra los productos destacados del carrusel de www.tablee.cl.</p>
        </div>
        <button type="button" onClick={openModal}>Agregar elemento</button>
      </header>

      <section className="tablee-carousel__display">
        <div className="tablee-carousel__display-head">
          <div>
            <span>Vista carrusel</span>
            <h2>Elementos publicados</h2>
          </div>
          <strong>{items.length} elementos</strong>
        </div>
        <div className="tablee-carousel__table-header" aria-hidden="true">
          <span>Imagen</span>
          <span>Contenido</span>
          <span>Estado</span>
          <span>Acciones</span>
        </div>

        <div className="tablee-carousel__items">
          {items.length === 0 ? (
            <p className="tablee-carousel__empty">No hay elementos en el carrusel.</p>
          ) : null}
          {items.map((item) => (
            <article key={item.id} className={!item.isActive ? "is-hidden" : ""}>
              <img src={item.imageUrl} alt="" />
              <div>
                <span>Orden {item.sortOrder}</span>
                <h2>{item.title}</h2>
                <p>{item.subtitle || "Sin bajada configurada."}</p>
                <small>
                  {item.linkedProductName ? `Producto: ${item.linkedProductName}` : "Sin producto vinculado"}
                </small>
              </div>
              <label className="tablee-carousel__switch">
                <input type="checkbox" checked={item.isActive} onChange={() => toggleItemActive(item)} />
                <span />
                <strong>{item.isActive ? "Activo" : "Oculto"}</strong>
              </label>
              <button type="button" className="tablee-carousel__danger" onClick={() => deleteItem(item)} aria-label={`Eliminar ${item.title}`}>
                <span className="material-symbols-outlined">delete</span>
              </button>
            </article>
          ))}
        </div>
      </section>

      {showModal ? (
        <div className="tablee-carousel__modal-backdrop" role="presentation" onMouseDown={closeModal}>
          <form
            className="tablee-carousel__modal"
            onSubmit={saveItem}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="tablee-carousel__modal-head">
              <div>
                <span>Tablee</span>
                <h2>Agregar al carrusel</h2>
              </div>
              <button type="button" className="tablee-carousel__icon-button" onClick={closeModal} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="tablee-carousel__form-grid">
              <label className="tablee-carousel__field tablee-carousel__wide-field">
                <span>Producto del catalogo</span>
                <div className="tablee-carousel__product-select">
                  <button
                    ref={productSelectTriggerRef}
                    type="button"
                    className="tablee-carousel__product-select-trigger"
                    aria-haspopup="listbox"
                    aria-expanded={isProductDropdownOpen}
                    onClick={() => {
                      setProductSearch("");
                      setIsProductDropdownOpen((current) => !current);
                    }}
                  >
                    <span className={selectedProduct ? "" : "is-placeholder"}>
                      {selectedProduct ? selectedProduct.name : "Seleccionar producto"}
                    </span>
                    <span className="material-symbols-outlined">expand_more</span>
                  </button>
                  {productDropdownMenu}
                </div>
              </label>
              <label className="tablee-carousel__field">
                <span>Orden</span>
                <input type="number" min="1" value={form.sortOrder} onChange={(event) => updateField("sortOrder", event.target.value)} />
              </label>
              {selectedProduct ? (
                <div className="tablee-carousel__product-preview tablee-carousel__wide-field">
                  <img src={selectedProductImage} alt="" />
                  <div>
                    <span>Producto seleccionado</span>
                    <strong>{selectedProduct.name}</strong>
                    <p>{selectedProduct.description || selectedProduct.category || "Sin descripcion configurada."}</p>
                    <small>{selectedProduct.unitLabel || "Catalogo Tablee"}</small>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="tablee-carousel__modal-actions">
              <div className="tablee-carousel__footer-left">
                <label className="tablee-carousel__footer-switch">
                  <input type="checkbox" checked={form.isActive} onChange={(event) => updateField("isActive", event.target.checked)} />
                  <span />
                  <strong>Publicado</strong>
                </label>
                {message ? <p>{message}</p> : null}
              </div>
              <div className="tablee-carousel__footer-buttons">
                <button type="button" className="tablee-carousel__secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit">Agregar producto</button>
              </div>
            </div>
          </form>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}

export default TableeCarousel;
